import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Box } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';
import {
  LayoutRectangle,
  NativeSyntheticEvent,
  Platform,
  TextInput as RNTextInput,
  TextInputChangeEventData,
} from 'react-native';

import ActionButton from 'components/action-button';
import { TextInput } from 'components/ucl/text-input';
import { GraphQLErrorMessages } from 'enums/graphql';
import { useNavigation } from 'hooks/navigation/use-navigation';
import useErrorModal from 'hooks/use-error-modal';
import { useOtpFeature } from 'hooks/use-otp-feature';
import { useEnableSignUpSignInABTest } from 'pages/authentication/use-enable-sign-up-sign-in-a-b-test';
import { HttpErrorCodes } from 'remote/constants';
import { useAuthContext } from 'state/auth';
import useAuthFlow from 'state/auth/hooks/use-auth-flow';
import { useLocale } from 'state/intl';
import { REGIONS } from 'state/intl/types';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLocationContext } from 'state/location';
import { EventTypes, useMParticleContext } from 'state/mParticle';
import { useScrollContext } from 'state/scroll';
import { useUIContext } from 'state/ui';
import { parseGraphQLErrorCodes } from 'utils/errors';
import { GraphQLErrorCodes } from 'utils/errors/types';
import { isEmailValid, isPhoneNumberValid } from 'utils/form';
import formatListOfWords, { JoinType } from 'utils/intl/formatListOfWords';
import logger from 'utils/logger';
import { OTPAuthDeliveryMethod } from 'utils/otp';
import { routes } from 'utils/routing';

import { IAuthCampaign } from '../components/auth-campaigns/hooks/use-auth-campaign';

import { StyledBoxActionButton, StyledButtonWidthLimit, TextInputWrapper } from './styled';

const { All, SMS, Email, None } = OTPAuthDeliveryMethod;

// Additional Y amount for show email input and submit button
const ADDITIONAL_SCROLL_Y = 50;

// we want to use the OTP flag value to adjust our messaging
const useFormattedMessages = () => {
  const { formatMessage } = useIntl();
  return (flagValue: OTPAuthDeliveryMethod) => {
    switch (flagValue) {
      case All:
        const combined = [
          formatMessage({ id: 'emailAddress' }),
          formatMessage({ id: 'phoneNumber' }),
        ];
        const combinedTitle = formatListOfWords({
          formatMessage,
          list: combined,
          joinType: JoinType.DISJUNCTION,
        });
        return [
          combinedTitle,
          formatMessage({ id: 'emailOrPhoneNumberRequiredError' }),
          formatMessage({ id: 'notValidEmailOrPhoneNumberError' }),
        ];
      case SMS:
        return [
          formatMessage({ id: 'phoneNumber' }),
          formatMessage({ id: 'phoneNumberRequiredError' }),
          formatMessage({ id: 'notValidPhoneNumberError' }),
        ];
      case Email:
      case None:
      default:
        return [
          formatMessage({ id: 'emailAddress' }),
          formatMessage({ id: 'emailRequiredError' }),
          formatMessage({ id: 'notValidEmailError' }),
        ];
    }
  };
};

interface ISignInFormProps {
  authCampaignExtraFields?: IAuthCampaign['extraFields'];
}

const SignInForm = ({ authCampaignExtraFields }: ISignInFormProps) => {
  const { signIn } = useAuthContext();
  const chooseMessages = useFormattedMessages();
  const { formatMessage } = useIntl();
  const { region } = useLocale() as { region: REGIONS };
  const { enableOtpFlagValue, emailOtpEnabled, jwtEmailEnabled, smsOtpEnabled } = useOtpFeature();
  const { attemptedSignUpWithExistingEmail, errorMessage: initialErrorMessage } = useAuthFlow();

  const { logRBIEvent } = useMParticleContext();

  const { userEmail } = useUIContext();
  const { signUpEmail } = useLocationContext();

  const emailInputEl = useRef<RNTextInput>(null);
  const enableNumericPhoneValidation = useFlag(LaunchDarklyFlag.ENABLE_NUMERIC_PHONE_VALIDATION);
  const enableUserNotFoundMaskAuthFlow = useFlag(
    LaunchDarklyFlag.ENABLE_USER_NOT_FOUND_MASK_ON_AUTH_FLOW
  );
  const enableSignUpSignInABTest = useEnableSignUpSignInABTest();
  const { navigate } = useNavigation();

  const defaultEmail = signUpEmail || userEmail || '';
  const [emailOrPhone, setEmailOrPhone] = useState(defaultEmail);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(initialErrorMessage);

  const [ErrorDialog, openErrDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Sign In Failure',
  });

  const aimToSignIn = useCallback(
    async ({ email = '', phoneNumber = '' }: any) => {
      try {
        setIsLoading(true);

        await signIn({
          email,
          phoneNumber,
          ...(email && { navigateOnSuccessParams: { email } }),
        });
        logRBIEvent({
          name: 'Sign in with OTP Request',
          type: EventTypes.Other,
          attributes: {
            signUpType: email ? 'Email' : 'Phone Number',
          },
        });
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        const createOtpError = enableUserNotFoundMaskAuthFlow
          ? GraphQLErrorCodes.CREATE_OTP_FAILED
          : GraphQLErrorCodes.AUTH_EMAIL_NOT_REGISTERED;
        // @ts-expect-error TS(2345) FIXME: Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
        const parsedErrors = parseGraphQLErrorCodes(error);

        if (
          parsedErrors.some(parsedError => parsedError.errorCode === createOtpError) ||
          // we need to handle both cases until all paths are migrated to apollo
          // @ts-expect-error TS(2571) FIXME: Object is of type 'unknown'.
          error.message === GraphQLErrorMessages.USER_NOT_FOUND
        ) {
          if (enableSignUpSignInABTest) {
            return navigate(routes.signUp, { state: { email } });
          }
          return setErrorMessage(formatMessage({ id: 'userDoesNotExist' }));
        }

        let message = formatMessage({ id: 'authError' });
        // @ts-expect-error TS(2571) FIXME: Object is of type 'unknown'.
        if (error?.graphQLErrors?.[0]?.extensions?.statusCode === HttpErrorCodes.TooManyRequests) {
          message = `${formatMessage({ id: 'maxAttemptsReached' })} ${formatMessage({
            id: 'pleaseTryAgainLater',
          })}`;
        }

        openErrDialog({
          // @ts-expect-error TS(2322) FIXME: Type 'unknown' is not assignable to type 'Error | ... Remove this comment to see the full error message
          error,
          message,
        });
        logger.warn({ error });
      }
    },
    [formatMessage, openErrDialog, signIn]
  );

  useEffect(() => {
    if (attemptedSignUpWithExistingEmail && !isLoading) {
      setEmailOrPhone(defaultEmail);
      aimToSignIn({ email: defaultEmail });
    }
  }, [defaultEmail, attemptedSignUpWithExistingEmail, aimToSignIn, isLoading]);

  const [labelMessage, requiredErrorMessage, invalidErrorMessage] = chooseMessages(
    enableOtpFlagValue
  );

  const handleEmailOrPhoneChange = (ev: NativeSyntheticEvent<TextInputChangeEventData>) => {
    setErrorMessage('');
    setEmailOrPhone(ev.nativeEvent.text);
  };

  const parse = useCallback(
    async (input: string) => {
      const trimmedInput = input.trim();
      if (!trimmedInput) {
        setErrorMessage(requiredErrorMessage);
        return {};
      }

      let isValidPhone;
      const noLetters = /^[\0-9]{2,}$/.test(trimmedInput);

      const isEmail = isEmailValid(trimmedInput);
      const isEmailEnabled = jwtEmailEnabled || emailOtpEnabled;

      if (!noLetters && !isEmail) {
        setErrorMessage(formatMessage({ id: 'notValidEmailError' }));
        return {};
      }

      if (noLetters && smsOtpEnabled) {
        isValidPhone = await isPhoneNumberValid({
          phoneNumber: trimmedInput,
          country: region,
          simpleValidation: enableNumericPhoneValidation,
        });
      }

      // Will accept "All Enabled", "SMS Enabled", and "Email Enabled"
      if (OTPAuthDeliveryMethod.All) {
        // Case for "Email Enabled"
        if (isEmailEnabled && isEmail) {
          return { email: trimmedInput };
          // Case for "SMS Enabled"
        } else if (smsOtpEnabled && isValidPhone?.valid) {
          return { phoneNumber: trimmedInput };
          // Case for "All Enabled"
        } else if (isEmail) {
          return { email: trimmedInput };
          // Case for "All Enabled"
        } else if (isValidPhone?.valid) {
          return { phoneNumber: trimmedInput };
        }
      }

      setErrorMessage(invalidErrorMessage);
      // If there was an error, try to focus the input after an attempted sign in
      // this will force screen readers to read the alert even if it never left the screen
      if (emailInputEl.current) {
        emailInputEl.current.focus();
      }
      return {};
    },
    [
      emailOtpEnabled,
      enableNumericPhoneValidation,
      invalidErrorMessage,
      jwtEmailEnabled,
      region,
      requiredErrorMessage,
      smsOtpEnabled,
    ]
  );

  const handleSubmit = useCallback(async () => {
    const { email, phoneNumber } = await parse(emailOrPhone);
    if (email || phoneNumber) {
      setErrorMessage('');
      aimToSignIn({ email, phoneNumber });
    }
  }, [aimToSignIn, emailOrPhone, parse]);

  const { scrollTo } = useScrollContext();
  const emailInputPosition = useRef<LayoutRectangle>();
  const handleEmailFocus = useCallback(() => {
    if (emailInputPosition.current && Platform.OS === 'android') {
      scrollTo({
        x: 0,
        y: emailInputPosition.current.y + emailInputPosition.current.height + ADDITIONAL_SCROLL_Y,
      });
    }
  }, [scrollTo]);

  return (
    <>
      <Box>
        <TextInputWrapper>
          <TextInput
            accessibilityLabel={errorMessage ? errorMessage : labelMessage}
            testID="signin-email-input"
            errorMessage={errorMessage}
            label={labelMessage}
            required
            type="email"
            onChange={handleEmailOrPhoneChange}
            ref={emailInputEl}
            autoComplete="email"
            value={emailOrPhone}
            autoCapitalize="none"
            keyboardType="email-address"
            returnKeyType="done"
            onSubmitEditing={handleSubmit}
            onLayout={ev => (emailInputPosition.current = ev.nativeEvent.layout)}
            onFocus={handleEmailFocus}
            placeholder={formatMessage({ id: 'emailAddress' })}
          />
        </TextInputWrapper>
        {authCampaignExtraFields}
        <StyledButtonWidthLimit>
          <StyledBoxActionButton>
            <ActionButton
              fullWidth
              testID="signin-button"
              isLoading={isLoading}
              disabled={isLoading}
              onPress={handleSubmit}
            >
              {enableSignUpSignInABTest
                ? formatMessage({ id: 'signInUp' })
                : formatMessage({ id: 'signIn' })}
            </ActionButton>
          </StyledBoxActionButton>
        </StyledButtonWidthLimit>
      </Box>

      <ErrorDialog />
    </>
  );
};

export default SignInForm;
